import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'

const Login = ({ onClose }) => {
  const [step, setStep] = useState('initial') // 'initial' or 'verification'
  const [email, setEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const { signInWithGoogle, sendVerificationCode, verifyCode } = useAuth()

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await sendVerificationCode(email)
      if (error) throw error

      setStep('verification')
      setMessage('Please check your email for the verification code.')
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { error } = await verifyCode(email, verificationCode)
      if (error) throw error
      onClose()
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError('')

    try {
      const { error } = await signInWithGoogle()
      if (error) throw error
      onClose()
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('initial')
    setVerificationCode('')
    setError('')
    setMessage('')
  }

  const handleResendCode = async () => {
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await sendVerificationCode(email)
      if (error) throw error
      setMessage('Please check your email for a new verification code.')
    } catch (error) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal to-upgrade" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <h2>
            {step === 'initial' ? 'Log in to continue' : 'Enter verification code'}
          </h2>
          {step === 'initial' && <p className='auth-intro-text'> Log in to unlock premium features or to access your subscription.</p>}
          <button className="close-btn" onClick={onClose}>
            <svg width={30} height={30} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.2} stroke="#211f1c">
              <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </button>
        </div>

        {error && <div className="error-message">
          <span>{error}</span>
        </div>}
        {message && <div className="success-message">
          <span>{message}</span>
        </div>}

        {step === 'initial' ? (
          <>
            <button
              onClick={handleGoogleSignIn}
              className="auth-btn google sec-btn svg---text"
              disabled={loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 33 33" fill="none">
                <path d="M31.86 16.8638C31.86 15.7292 31.7582 14.6383 31.5691 13.5911H16.5V19.7874H25.1109C24.7327 21.7802 23.5982 23.4674 21.8964 24.602V28.6311H27.0891C30.1145 25.8383 31.86 21.7365 31.86 16.8638Z" fill="#4285F4" />
                <path d="M16.5004 32.5019C20.8204 32.5019 24.4423 31.0764 27.0895 28.6328L21.8968 24.6037C20.4713 25.5637 18.6532 26.1455 16.5004 26.1455C12.3404 26.1455 8.80589 23.3382 7.54043 19.5564H2.2168V23.6873C4.84952 28.9091 10.2459 32.5019 16.5004 32.5019Z" fill="#34A853" />
                <path d="M7.54 19.5407C7.22 18.5807 7.03091 17.5626 7.03091 16.5007C7.03091 15.4389 7.22 14.4207 7.54 13.4607V9.32983H2.21636C1.12545 11.4826 0.5 13.9117 0.5 16.5007C0.5 19.0898 1.12545 21.5189 2.21636 23.6717L6.36182 20.4426L7.54 19.5407Z" fill="#FBBC05" />
                <path d="M16.5004 6.87091C18.8568 6.87091 20.9513 7.68545 22.6241 9.25636L27.2059 4.67455C24.4277 2.08545 20.8204 0.5 16.5004 0.5C10.2459 0.5 4.84952 4.09273 2.2168 9.32909L7.54043 13.46C8.80589 9.67818 12.3404 6.87091 16.5004 6.87091Z" fill="#EA4335" />
              </svg>
              <span>Continue with Google</span>
            </button>

            <div className="divider">
              <span>OR</span>
            </div>

            <form onSubmit={handleEmailSubmit}>
              <div className="form-group">
                <label htmlFor="email">Use your email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="auth-btn pri-btn"
                disabled={loading}
              >
                <span>{loading ? 'Sending...' : 'Continue'}</span>
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="verification-info">
              <p>We sent a verification code to:</p>
              <p>{email}</p>
            </div>

            <form onSubmit={handleVerificationSubmit}>
              <div className="form-group">
                <label htmlFor="code">Verification code</label>
                <input
                  id="code"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  disabled={loading}
                  maxLength={6}
                  pattern="[0-9]{6}"
                />
              </div>

              <button
                type="submit"
                className="auth-btn pri-btn"
                disabled={loading || verificationCode.length !== 6}
              >
                <span>{loading ? 'Verifying...' : 'Verify & Continue'}</span>
              </button>
            </form>

            <div className="auth-switch">
              <button
                type="button"
                onClick={handleBackToEmail}
                className="link-btn"
                disabled={loading}
              >
                Back to email
              </button>
              <span> | </span>
              <button
                type="button"
                onClick={handleResendCode}
                className="link-btn"
                disabled={loading}
              >
                Resend code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Login